/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { memo, useCallback, useMemo } from 'react';
import {
  Pressable,
  Platform,
  ScrollView,
  View,
  useWindowDimensions,
  type ViewStyle,
} from 'react-native';
import { createPortal } from 'react-dom';
import TextView from '@airasia-phoenix/react-native/components/TextView';
import {
  getColor,
  getTextStyle,
  pd,
  getShape,
} from '@airasia-phoenix/react-native/utils/utils';
import { StyleSheet } from 'react-native-unistyles';
import { useTranslation } from 'react-i18next';
import BottomSheet from '@airasia-phoenix/react-native/components/BottomSheetView';
import { useBottomSheetBackdropWebFix } from '../../hooks/useBottomSheetBackdropWebFix';
import { BottomSheetScrollView } from '@gorhom/bottom-sheet';
import IconView from '@airasia-phoenix/react-native/components/IconView';
import { Portal } from '@gorhom/portal';

import { getChildAgeLabel } from './utils';
import { ChildAgePickerProps } from '../../types';
import {
  CHILD_AGE_PICKER_CONSTANTS,
  AGE_PICKER_SHEET_HEIGHT,
} from './constants';

const CHILD_AGE_PORTAL_CARD_ID = 'hotelChildAgePortalCard';

const AgeOption = memo(
  ({
    age,
    isSelected,
    onPress,
    testID,
  }: {
    age: number;
    isSelected: boolean;
    onPress: (age: number) => void;
    testID: string;
  }) => {
    const { t } = useTranslation('hotels');
    return (
      <Pressable
        testID={`${testID}-item-${age}`}
        style={styles.ageItem as unknown as ViewStyle}
        onPress={() => onPress(age)}
      >
        <TextView
          text={getChildAgeLabel(age, t)}
          style={getTextStyle(
            isSelected
              ? pd.sys.typescale.body_text_medium
              : pd.sys.typescale.body_text,
            isSelected
              ? pd.sys.colour.secondary
              : pd.sys.colour.content_primary,
          )}
        />
        {isSelected && (
          <IconView
            icon={pd.icon.main.navigation.check}
            tint={getColor(pd.sys.colour.content_primary)}
            width={pd.ref.space.spacing50.px}
            height={pd.ref.space.spacing50.px}
          />
        )}
      </Pressable>
    );
  },
);

AgeOption.displayName = 'AgeOption';

const ChildAgePickerSheet = memo(
  ({
    bottomSheetRef,
    onClose,
    onSelectAge,
    selectedAge,
    title,
    isVisible,
    onRequestClose,
    anchorLayout,
    testID = 'child-age-picker-sheet',
    config,
    isParentModal = false,
  }: ChildAgePickerProps & { testID?: string }) => {
    const { width: windowWidth, height: windowHeight } = useWindowDimensions();

    const shouldShowAsModal = isParentModal;

    useBottomSheetBackdropWebFix(!!isVisible && !shouldShowAsModal);

    const webCardStyle = useMemo(() => {
      if (!shouldShowAsModal || !anchorLayout) return null;

      const cardWidth = CHILD_AGE_PICKER_CONSTANTS.WEB_CARD_WIDTH;
      const edge = CHILD_AGE_PICKER_CONSTANTS.EDGE_MARGIN;
      const gap = CHILD_AGE_PICKER_CONSTANTS.GAP;

      const left = Math.max(
        edge,
        Math.min(anchorLayout.x, windowWidth - cardWidth - edge),
      );

      const top = anchorLayout.y + anchorLayout.height + gap;
      const availableSpaceBelow = windowHeight - top - 50;
      const dropdownHeight = 300;

      const finalTop = Math.max(
        edge,
        availableSpaceBelow < dropdownHeight
          ? anchorLayout.y - gap - dropdownHeight
          : top,
      );

      return {
        position: Platform.select({
          web: 'fixed',
          default: 'absolute',
        }) as unknown as ViewStyle['position'],
        top: finalTop,
        left,
        zIndex: CHILD_AGE_PICKER_CONSTANTS.WEB_CARD_Z_INDEX,
      };
    }, [shouldShowAsModal, anchorLayout, windowWidth, windowHeight]);

    const handleSelect = useCallback(
      (age: number) => {
        onSelectAge(age);
        if (shouldShowAsModal) {
          onRequestClose?.();
        } else {
          bottomSheetRef?.current?.toggleBottomSheetView();
        }
      },
      [onSelectAge, bottomSheetRef, onRequestClose, shouldShowAsModal],
    );

    if (shouldShowAsModal) {
      if (!isVisible) return null;

      const modalContent = (
        <View
          style={styles.webBackdrop as unknown as ViewStyle}
          testID={`${testID}-backdrop`}
        >
          <Pressable
            testID={`${testID}-backdrop-touchable`}
            style={styles.backdropTouchable as unknown as ViewStyle}
            onPress={() => onRequestClose?.()}
          />
          <View
            nativeID={CHILD_AGE_PORTAL_CARD_ID}
            style={[
              styles.webCard as unknown as ViewStyle,
              webCardStyle ?? undefined,
            ]}
            testID={`${testID}-card`}
          >
            <ScrollView
              style={styles.webScroll as unknown as ViewStyle}
              contentContainerStyle={
                styles.webScrollContent as unknown as ViewStyle
              }
              showsVerticalScrollIndicator={false}
              nestedScrollEnabled
              keyboardShouldPersistTaps="handled"
            >
              {config.AGE_OPTIONS.map((age: number) => (
                <AgeOption
                  key={age}
                  age={age}
                  isSelected={age === selectedAge}
                  onPress={handleSelect}
                  testID={testID}
                />
              ))}
            </ScrollView>
          </View>
        </View>
      );

      if (typeof document !== 'undefined' && document.body) {
        return createPortal(modalContent, document.body);
      }

      return <Portal hostName="hotels-web-portal-host">{modalContent}</Portal>;
    }
    if (!bottomSheetRef) return null;

    const handleCloseBottomSheet = useCallback(() => {
      bottomSheetRef?.current?.toggleBottomSheetView();
      onClose();
    }, [bottomSheetRef, onClose]);

    return (
      <BottomSheet
        ref={bottomSheetRef}
        sectionTitle={title}
        onCloseIconPress={handleCloseBottomSheet}
        onBottomSheetClose={onClose}
        enablePanDownToClose={Platform.OS !== 'web'}
        enableOverDrag={false}
        enableHandlePanningGesture={false}
        enableContentPanningGesture={true}
        showHeader
        showBackdrop
        closeIconProps={{ icon: pd.icon.main.navigation.close }}
        initialHeight={AGE_PICKER_SHEET_HEIGHT}
        finalHeight={AGE_PICKER_SHEET_HEIGHT}
        height={windowHeight * 0.6}
      >
        <BottomSheetScrollView
          contentContainerStyle={styles.ageScrollView as unknown as ViewStyle}
          showsVerticalScrollIndicator
          keyboardShouldPersistTaps="handled"
          nestedScrollEnabled
        >
          {config.AGE_OPTIONS.map((age: number) => (
            <AgeOption
              key={age}
              age={age}
              isSelected={age === selectedAge}
              onPress={handleSelect}
              testID={testID}
            />
          ))}
        </BottomSheetScrollView>
      </BottomSheet>
    );
  },
);

ChildAgePickerSheet.displayName = 'ChildAgePickerSheet';

const styles = StyleSheet.create({
  ageItem: {
    paddingVertical: pd.ref.space.spacing30.px,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  ageScrollView: {
    paddingLeft: pd.ref.space.spacing40.px,
    paddingRight: pd.ref.space.spacing40.px,
    paddingTop: pd.ref.space.spacing10.px,
    paddingBottom: pd.ref.space.spacing80.px,
  },
  webBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: CHILD_AGE_PICKER_CONSTANTS.WEB_BACKDROP_Z_INDEX,
  },
  backdropTouchable: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  webCard: {
    width: CHILD_AGE_PICKER_CONSTANTS.WEB_CARD_WIDTH,
    maxHeight: CHILD_AGE_PICKER_CONSTANTS.WEB_CARD_MAX_HEIGHT,
    ...getShape(pd.sys.shape.corner.medium),
    overflow: 'hidden',
    backgroundColor: getColor(pd.sys.colour.container_none),
    borderWidth: CHILD_AGE_PICKER_CONSTANTS.BORDER_WIDTH,
    borderColor: getColor(pd.sys.colour.outline_medium),
    shadowColor: CHILD_AGE_PICKER_CONSTANTS.SHADOW_COLOR,
    shadowOpacity: CHILD_AGE_PICKER_CONSTANTS.SHADOW_OPACITY,
    shadowRadius: CHILD_AGE_PICKER_CONSTANTS.SHADOW_RADIUS,
    shadowOffset: {
      width: 0,
      height: CHILD_AGE_PICKER_CONSTANTS.SHADOW_OFFSET_HEIGHT,
    },
  },
  webScroll: {
    flex: 1,
  },
  webScrollContent: {
    paddingHorizontal: pd.ref.space.spacing20.px,
  },
});

export default ChildAgePickerSheet;
